import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injectable,
  Input,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MatDateFormats,
  NativeDateAdapter,
} from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * Dates as the calendars of the app show them: dd/mm/yyyy, or the year alone
 * for the year fields. The week starts on Monday. Each date input has its own
 * adapter, as the format depends on the field.
 */
@Injectable()
export class AppDateAdapter extends NativeDateAdapter {
  yearOnly = false;

  override getFirstDayOfWeek(): number {
    return 1;
  }

  override format(date: Date, displayFormat: object): string {
    if (displayFormat !== APP_DATE_FORMATS.display.dateInput) {
      return super.format(date, displayFormat);
    }
    const year = String(date.getFullYear());
    return this.yearOnly
      ? year
      : `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${year}`;
  }

  override parse(value: unknown, parseFormat?: unknown): Date | null {
    if (typeof value !== 'string') {
      return super.parse(value, parseFormat);
    }
    const text = value.trim();
    if (!text) {
      return null;
    }
    const year = /^(\d{4})$/.exec(text);
    if (year) {
      return new Date(+year[1], 0, 1);
    }
    const day = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
    if (day && !this.yearOnly) {
      const date = new Date(+day[3], +day[2] - 1, +day[1]);
      // 31/02 would roll over into March.
      if (date.getDate() === +day[1] && date.getMonth() === +day[2] - 1) {
        return date;
      }
    }
    return this.invalid();
  }
}

export const APP_DATE_FORMATS: MatDateFormats = {
  parse: { dateInput: 'app' },
  display: {
    dateInput: { app: true },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

/**
 * A date field with the Material datepicker: the input, a clear icon and the
 * calendar button. `view="year"` picks a year (1 January of it), otherwise a
 * day. The value is a Date.
 */
@Component({
  selector: 'app-date-input',
  standalone: true,
  imports: [MatDatepickerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },
  ],
  template: `<span
    class="app-date-input {{ styleClass }}"
    [class.app-date-input-w-btn]="showIcon"
    [class.app-date-input-clearable]="showClear && !disabled"
    [class.app-focus]="focused"
    [class.app-disabled]="disabled"
  >
    <input
      class="app-input app-date-input-field"
      type="text"
      autocomplete="off"
      [matDatepicker]="picker"
      [value]="value"
      [attr.placeholder]="placeholder"
      [attr.aria-label]="ariaLabel || placeholder"
      [disabled]="disabled"
      (dateInput)="onDateInput($event)"
      (click)="showOnFocus && picker.open()"
      (focus)="focused = true"
      (blur)="onBlur()"
    />
    @if (showClear && !disabled && value) {
      <i
        class="app-date-input-clear app-icon fa-solid fa-xmark"
        aria-hidden="true"
        (click)="clear()"
      ></i>
    }
    @if (showIcon) {
      <button
        type="button"
        class="app-date-input-trigger"
        aria-label="Choose date"
        [disabled]="disabled"
        (click)="picker.open()"
      >
        <span class="app-date-input-trigger-icon {{ icon }}"></span>
      </button>
    }
    <mat-datepicker
      #picker
      panelClass="app-date-input-panel"
      [startView]="view === 'year' ? 'multi-year' : 'month'"
      (yearSelected)="onYearSelected($event)"
      (closed)="onTouched()"
    ></mat-datepicker>
  </span>`,
})
export class DateInputComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);
  private adapter = inject(DateAdapter) as AppDateAdapter;

  @Input() placeholder?: string;
  @Input() showIcon = false;
  @Input() showClear = false;
  /** Opens the calendar when the field is clicked. */
  @Input() showOnFocus = true;
  @Input() icon = 'app-icon fa-regular fa-calendar';
  @Input() styleClass = '';
  @Input() ariaLabel?: string;
  @Input() disabled = false;

  /** 'year' picks years, 'date' days. */
  @Input() set view(view: 'date' | 'year') {
    this._view = view;
    this.adapter.yearOnly = view === 'year';
  }
  get view() {
    return this._view;
  }
  private _view: 'date' | 'year' = 'date';

  @ViewChild('picker', { static: true })
  private picker!: MatDatepicker<Date>;

  value: Date | null = null;
  focused = false;

  private onChange: (value: Date | null) => void = () => undefined;
  onTouched: () => void = () => undefined;

  writeValue(value: Date | null): void {
    this.value = value instanceof Date ? value : null;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.cdr.markForCheck();
  }

  /**
   * A typed date or a day picked in the calendar. Text that is no date (yet)
   * empties the value.
   */
  onDateInput(event: MatDatepickerInputEvent<Date>) {
    const next =
      event.value && this.adapter.isValid(event.value) ? event.value : null;
    if (next?.getTime() !== this.value?.getTime()) {
      this.setValue(next);
    }
  }

  onYearSelected(year: Date) {
    if (this.view === 'year') {
      this.setValue(new Date(year.getFullYear(), 0, 1));
      this.picker.close();
    }
  }

  onBlur() {
    this.focused = false;
    if (!this.picker.opened) {
      this.onTouched();
    }
  }

  clear() {
    this.setValue(null);
  }

  private setValue(value: Date | null) {
    this.value = value;
    this.onChange(value);
    this.cdr.markForCheck();
  }
}
